// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { currencySchema } from '@/currency/api/types';
import { z } from 'zod';

const validationErrorFieldSchema = z.object({
  fieldName: z.string(),
  error: z.string(),
  errorCode: z.number(),
});

const rowValidationErrorSchema = z.object({
  index: z.number(),
  errors: z.array(validationErrorFieldSchema),
});

const processedShipmentImportDataSchema = z.object({
  index: z.number(),
  shipmentNumber: z.string(),
  shippingCost: currencySchema.nullable(),
  fieldsUpdated: z.record(z.boolean()),
  errors: z.array(validationErrorFieldSchema).nullable(),
});

const shipmentDataUploadResponseSchema = z.object({
  success: z.boolean(),
  items: z.array(processedShipmentImportDataSchema),
  structureErrors: z.array(validationErrorFieldSchema),
  rowErrors: z.array(rowValidationErrorSchema),
});

export const uploadShipmentDataResponseSchema = z.object({
  data: shipmentDataUploadResponseSchema,
  metadata: responseMetadataSchema,
});

export type ShipmentDataUploadResult = z.infer<
  typeof shipmentDataUploadResponseSchema
>;

export async function uploadShipmentData(
  formData: FormData
): Promise<ShipmentDataUploadResult> {
  const url = '/v1/import/shipment-data';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const parsed = await parseResponse(
    uploadShipmentDataResponseSchema,
    response.data,
    {
      schemaName: 'UploadShipmentData',
      endpoint: url,
    }
  );
  return parsed.data;
}
